import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { TotpEnroll } from "@/components/auth/totp";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { operatorSetupAdapter } from "@/lib/api-2fa";
import { consumeJustLoggedOut } from "@/lib/session";
import { useAuthStore } from "@/lib/auth";
import { fetchTenantContext } from "@/lib/platform";

interface SessionUser {
  user_id: string;
  username: string;
  email: string;
  full_name?: string | null;
  groups: string[];
}

/** Polymorphic login response (ADR-0017): `ok` carries a session; `totp_*`
 *  carries a challenge / setup token and no session. */
interface LoginResponse {
  status?: "ok" | "totp_required" | "totp_setup_required";
  access_token: string | null;
  refresh_token?: string | null;
  token_type: string;
  user: SessionUser | null;
  challenge_token?: string | null;
  setup_token?: string | null;
}

type Stage =
  | { kind: "credentials" }
  | { kind: "totp"; challengeToken: string }
  | { kind: "setup"; setupToken: string; begin: { secret: string; otpauth_uri: string } };

export function LoginPage() {
  const { t } = useTranslation();
  // Where to land after login. ProtectedRoute stashes the page that
  // bounced here (session expiry included) in location.state.from, so
  // an operator kicked out mid-work returns exactly where they were.
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const landing = from?.pathname ? `${from.pathname}${from.search ?? ""}` : "/dashboard";
  const setSession = useAuthStore((s) => s.setSession);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [stage, setStage] = React.useState<Stage>({ kind: "credentials" });
  const [code, setCode] = React.useState("");

  function finish(res: LoginResponse) {
    if (res.access_token && res.user) {
      setSession(res.access_token, res.user, res.refresh_token ?? null);
      // Hard reload (not SPA navigate): a login is an identity change, so
      // we tear down ALL in-memory state — React Query cache (the cached
      // ["me"] permissions that drove the wrong sidebar), the SSE stream
      // opened as the previous user, every module singleton — and reboot
      // clean as the new operator. The session re-hydrates from
      // localStorage (persisted auth store).
      window.location.replace(landing);
    }
  }

  /** Forced-setup confirm hands back a session token but no user — fetch the
   *  profile with that token, then open the session. */
  async function completeWithToken(token: string) {
    const me = await api.get<SessionUser>("/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Forced-2FA-setup path mints no refresh token — that session just
    // expires at the access TTL; the next normal login gets the pair.
    setSession(token, me, null);
    window.location.replace(landing); // hard reload — see finish()
  }

  // The SaaS double door (ADR-0077 S2): on a subdomain-resolved tenant the probe
  // returns `central_login_url` and the primary door is the Account Tray login on
  // `app.<base>` (with a `?next=<slug>` return-to). On-prem/apex it stays absent,
  // so the local form is the only door — unchanged by construction.
  const [centralUrl, setCentralUrl] = React.useState<string | null>(null);
  // Read ONCE per mount: signing out of a workspace does not sign you out of the Account
  // Tray, so the very next "Accedi con Account Tray" walks back in with no prompt. That is
  // correct (one identity, many workspaces) and reads as a broken logout unless we say it.
  const [justLoggedOut] = React.useState(consumeJustLoggedOut);
  // ADR-0077 S3: the per-workspace local-door toggle. The local form hides ONLY
  // when the central door exists AND the probe says the door is closed — a
  // failed probe (fail-open true) or an on-prem host never hides the only door.
  const [localDoor, setLocalDoor] = React.useState(true);
  React.useEffect(() => {
    void fetchTenantContext().then((ctx) => {
      if (ctx.central_login_url) {
        const next = ctx.slug ? `?next=${encodeURIComponent(ctx.slug)}` : "";
        setCentralUrl(`${ctx.central_login_url}${next}`);
        setLocalDoor(ctx.local_login_enabled !== false);
      }
    });
  }, []);
  const showLocalForm = localDoor || !centralUrl;

  function mapError(err: unknown): string {
    if (err instanceof ApiError) {
      // A central-account username on the local form: route the person to the
      // Account Tray door instead of a misleading "invalid credentials".
      if (err.message.includes("auth:central_account"))
        return t(
          "auth.centralAccount",
          "Questo account accede con Account Tray: usa il pulsante qui sopra.",
        );
      // The workspace closed its local door while this form was open (ADR-0077 S3).
      if (err.message.includes("auth:local_login_disabled"))
        return t(
          "auth.localDisabled",
          "Questo workspace usa solo l'accesso con Account Tray.",
        );
      if (err.status === 401 || err.status === 403) return t("auth.invalidCredentials");
      return err.message;
    }
    return "Unexpected error";
  }

  async function handleCredentials(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<LoginResponse>("/auth/login", { username, password });
      if (res.status === "totp_required" && res.challenge_token) {
        setStage({ kind: "totp", challengeToken: res.challenge_token });
      } else if (res.status === "totp_setup_required" && res.setup_token) {
        // Forced enrollment: mint the QR with the setup token, then enroll.
        const begin = await operatorSetupAdapter(res.setup_token).begin();
        setStage({ kind: "setup", setupToken: res.setup_token, begin });
      } else {
        finish(res);
      }
    } catch (err) {
      setError(mapError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleTotp(event: React.FormEvent) {
    event.preventDefault();
    if (stage.kind !== "totp") return;
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<LoginResponse>("/auth/login/totp", {
        challenge_token: stage.challengeToken,
        code: code.trim(),
      });
      finish(res);
    } catch (err) {
      setError(mapError(err));
    } finally {
      setLoading(false);
    }
  }

  // Cross-subdomain login handoff (ADR-0041): the unified login on `app.<base>`
  // redirects here with a `?handoff=1` FLAG (the secret rides in a HttpOnly cookie,
  // NOT the URL). We POST the exchange — the browser sends the cookie automatically
  // (same-origin) — and the hard-redirect (finish) lands the operator in. A dead/
  // missing cookie 401s → fall back to the form.
  const hasHandoff = React.useMemo(
    () => new URLSearchParams(location.search).get("handoff") === "1",
    [location.search],
  );
  const [exchanging, setExchanging] = React.useState<boolean>(hasHandoff);
  const exchangedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasHandoff || exchangedRef.current) return;
    exchangedRef.current = true;
    void (async () => {
      try {
        // Empty body — the handoff token is in the HttpOnly cookie the server reads.
        const res = await api.post<LoginResponse>("/auth/login/handoff", {});
        finish(res); // hard-redirect on success
      } catch {
        setError(t("auth.handoffExpired", "Il link di accesso è scaduto. Accedi di nuovo."));
        setExchanging(false);
        // Strip the flag so a refresh doesn't retry a consumed/dead handoff.
        window.history.replaceState(null, "", window.location.pathname);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHandoff]);

  if (exchanging) {
    return (
      <div className="grid h-full place-items-center bg-bg">
        <p className="text-sm text-muted-foreground">{t("auth.signingIn", "Accesso in corso…")}</p>
      </div>
    );
  }

  return (
    <div className="grid h-full place-items-center bg-bg">
      <div className="w-full max-w-sm rounded-lg border border-border/60 bg-elevated shadow-lg">
        <div className="flex h-14 items-center gap-2.5 border-b border-border px-4">
          <img
            src="/tray-logo.svg"
            alt=""
            aria-hidden="true"
            width={28}
            height={28}
            className="h-7 w-7 shrink-0"
          />
          <span
            className="text-base font-semibold tracking-tight"
            style={{ fontFamily: "Inter, system-ui, sans-serif" }}
          >
            Tray
          </span>
        </div>

        {justLoggedOut && centralUrl && (
          <Alert tone="info" className="mx-4 mt-4">
            {t(
              "auth.loggedOutStillCentral",
              "Sei uscito da questo workspace, ma il tuo Account Tray è ancora connesso: " +
                "«Accedi con Account Tray» rientrerà senza chiedere le credenziali. Per " +
                "uscire davvero, esci anche dall'Account Tray.",
            )}
          </Alert>
        )}

        {stage.kind === "credentials" && !showLocalForm && (
          // Local door CLOSED (every operator is central): the Account Tray
          // button is the only affordance — no misleading username form.
          <div className="space-y-3 p-4">
            <Button asChild className="w-full">
              <a href={centralUrl!}>{t("auth.centralLogin", "Accedi con Account Tray")}</a>
            </Button>
            {error && <Alert tone="danger">{error}</Alert>}
            <p className="pt-1 text-center text-xs text-muted-foreground">
              {t(
                "auth.localDisabled",
                "Questo workspace usa solo l'accesso con Account Tray.",
              )}
            </p>
          </div>
        )}

        {stage.kind === "credentials" && showLocalForm && (
          <form onSubmit={handleCredentials} className="space-y-3 p-4">
            {centralUrl && (
              <>
                <Button asChild variant="outline" className="w-full">
                  <a href={centralUrl}>{t("auth.centralLogin", "Accedi con Account Tray")}</a>
                </Button>
                <div className="flex items-center gap-3 py-1">
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">
                    {t("auth.orLocal", "oppure con un account locale")}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <Alert tone="danger" title="Authentication failed">
                {error}
              </Alert>
            )}

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? "Signing in…" : t("auth.signIn")}
            </Button>

            <p className="pt-1 text-center text-xs text-muted-foreground">
              {t("auth.loginSubtitle")}
            </p>
          </form>
        )}

        {stage.kind === "totp" && (
          <form onSubmit={handleTotp} className="space-y-3 p-4">
            <div className="space-y-1.5">
              <Label htmlFor="login-code">{t("twoFactor.enterCode")}</Label>
              <p className="text-xs text-muted-foreground">{t("twoFactor.loginHelp")}</p>
              <Input
                id="login-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="text"
                autoComplete="one-time-code"
                placeholder="000000"
                className="font-mono tracking-widest"
                autoFocus
              />
            </div>
            {error && <Alert tone="danger">{error}</Alert>}
            <Button type="submit" disabled={loading || !code.trim()} className="w-full">
              {loading ? "…" : t("twoFactor.verify")}
            </Button>
          </form>
        )}

        {stage.kind === "setup" && (
          <div className="space-y-3 p-4">
            <div>
              <h2 className="text-sm font-semibold">{t("twoFactor.setupRequiredTitle")}</h2>
              <p className="text-xs text-muted-foreground">{t("twoFactor.setupRequiredHelp")}</p>
            </div>
            <TotpEnroll
              begin={stage.begin}
              onConfirm={(c) => operatorSetupAdapter(stage.setupToken).confirm(c)}
              onDone={(result) => {
                if (result.access_token) void completeWithToken(result.access_token);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
